export const HOST_DOCKING_CONTAINER_NAME = "phoebe-host-document";
const HOST_DOCKING_DATA_ATTRIBUTE = "data-phoebe-host-docking";
const HOST_DOCKING_STYLE_ATTRIBUTE = "data-phoebe-host-docking-generated";
export const HOST_DOCKING_RAIL_PX = 446;
export const MOBILE_BREAKPOINT_PX = 576;

export const HOST_DOCKING_LIMITS = {
  maxStylesheets: 64,
  maxCandidateRules: 2_000,
  maxGeneratedCssCharacters: 512_000,
  maxGeneratedStyles: 64,
  maxBatchRules: 100,
  maxBatchMilliseconds: 8,
  maxTotalProcessingMilliseconds: 200,
} as const;

export type HostDockingLeaseState = { open: boolean; desktop: boolean };
export type HostDockingDiagnostics = {
  converted: number;
  skippedUnreadable: number;
  skippedUnsupported: number;
  skippedResourceValue: number;
  skippedViewportUnit: number;
  skippedRootConflict: number;
  skippedBudget: number;
  failedInsertion: number;
};
export type HostDockingDiagnosticsListener = (diagnostics: Readonly<HostDockingDiagnostics>) => void;
export type HostDockingOptions = { onDiagnostics?: HostDockingDiagnosticsListener };
export type HostDockingLease = {
  setState(state: HostDockingLeaseState): void;
  update(state: HostDockingLeaseState): void;
  release(): void;
};
export type HostDockingSnapshot = {
  desktopLeaseCount: number;
  geometryActive: boolean;
  conversionInstalled: boolean;
  generatedRuleCount: number;
  observing: boolean;
  diagnostics: Readonly<HostDockingDiagnostics>;
};
export type HostDockingController = {
  acquireLease(initialState?: HostDockingLeaseState): HostDockingLease;
  reconcileNow(): void;
  getDiagnostics(): Readonly<HostDockingDiagnostics>;
  getSnapshot(): HostDockingSnapshot;
};

type SourceRecord = {
  fingerprint: string;
  generatedStyle: HTMLStyleElement;
  cssCharacters: number;
  ruleCount: number;
  failures: number;
};

const controllerByDocument = new WeakMap<Document, HostDockingControllerImpl>();
let nextContainerId = 0;

const emptyDiagnostics = (): HostDockingDiagnostics => ({
  converted: 0,
  skippedUnreadable: 0,
  skippedUnsupported: 0,
  skippedResourceValue: 0,
  skippedViewportUnit: 0,
  skippedRootConflict: 0,
  skippedBudget: 0,
  failedInsertion: 0,
});

function canUseDocument(documentRef: Document | undefined): documentRef is Document {
  return typeof documentRef !== "undefined" && typeof documentRef.createElement === "function";
}

function now(): number {
  return typeof performance !== "undefined" ? performance.now() : Date.now();
}

function isWidgetOwnedElement(element: Element | null): boolean {
  return Boolean(
    element?.closest(
      `#phoebe-chat-root, .aui-root, [data-radix-portal], [${HOST_DOCKING_STYLE_ATTRIBUTE}], [data-phoebe-widget-owned]`,
    ),
  );
}

function isWidgetOwnedStylesheet(sheet: CSSStyleSheet): boolean {
  const ownerNode = sheet.ownerNode;
  if (ownerNode instanceof HTMLStyleElement && ownerNode.textContent?.includes("#phoebe-chat-root")) return true;
  return Boolean(ownerNode && "closest" in ownerNode && isWidgetOwnedElement(ownerNode as Element));
}

function isStyleRule(rule: CSSRule): rule is CSSStyleRule {
  return rule.type === CSSRule.STYLE_RULE && "selectorText" in rule && "style" in rule;
}

function isMediaRule(rule: CSSRule): rule is CSSMediaRule {
  return rule.type === CSSRule.MEDIA_RULE && "media" in rule && "cssRules" in rule;
}

function hasNestedRules(rule: CSSStyleRule): boolean {
  try {
    return "cssRules" in rule && Boolean((rule as CSSStyleRule & { cssRules?: CSSRuleList }).cssRules?.length);
  } catch {
    return true;
  }
}

function supportedContainerCondition(mediaText: string): string | null {
  const match = mediaText.trim().replace(/\s+/g, " ").match(/^\(\s*max-(width|inline-size)\s*:\s*(0|(?:\d+(?:\.\d+)?|\.\d+)px)\s*\)$/i);
  return match ? `(max-${match[1].toLowerCase()}: ${match[2]})` : null;
}

function hasResourceCapableValue(declarations: string): boolean {
  return /(?:url|image-set|-webkit-image-set|var|attr)\s*\(/i.test(declarations);
}

function hasViewportWidthUnit(declarations: string): boolean {
  return /(?:^|[^\w.-])[-+]?(?:\d+(?:\.\d+)?|\.\d+)(?:dvw|svw|lvw|vw|dvi|svi|lvi|vi|vmin|vmax)\b/i.test(
    declarations,
  );
}

function splitSelectorList(selector: string): string[] | null {
  const parts: string[] = [];
  let start = 0;
  let quote = "";
  let escaped = false;
  let depth = 0;
  for (let index = 0; index < selector.length; index += 1) {
    const character = selector[index];
    if (escaped) {
      escaped = false;
      continue;
    }
    if (character === "\\") {
      escaped = true;
      continue;
    }
    if (quote) {
      if (character === quote) quote = "";
      continue;
    }
    if (character === "'" || character === '"') {
      quote = character;
      continue;
    }
    if (character === "(" || character === "[") depth += 1;
    if (character === ")" || character === "]") {
      depth -= 1;
      if (depth < 0) return null;
    }
    if (character === "," && depth === 0) {
      parts.push(selector.slice(start, index).trim());
      start = index + 1;
    }
  }
  if (quote || escaped || depth !== 0) return null;
  parts.push(selector.slice(start).trim());
  return parts.every(Boolean) ? parts : null;
}

function rewriteSelector(selector: string): string | null {
  if (!selector || /:has\(|:root\b/i.test(selector)) return null;
  if (/(^|[\s>+~,])(?:\*|html|body|button|input|textarea|select|option|a|form|header|footer|nav|main|div)(?=[\s.#:[>+~]|$)/i.test(selector)) {
    return null;
  }
  const pseudoElement = selector.search(/::[\w-]+(?:\([^)]*\))?\s*$/);
  const beforePseudoElement = pseudoElement === -1 ? selector : selector.slice(0, pseudoElement);
  const afterPseudoElement = pseudoElement === -1 ? "" : selector.slice(pseudoElement);
  if (!/[.#[]|:(?:is|not|where)\(/.test(beforePseudoElement)) return null;
  return `${beforePseudoElement}:where(:not(#phoebe-chat-root, #phoebe-chat-root *, .aui-root, .aui-root *, [data-radix-portal], [data-radix-portal] *, [data-radix-popper-content-wrapper], [data-radix-popper-content-wrapper] *, [${HOST_DOCKING_STYLE_ATTRIBUTE}], [${HOST_DOCKING_STYLE_ATTRIBUTE}] *, [data-phoebe-widget-owned], [data-phoebe-widget-owned] *))${afterPseudoElement}`;
}

function safeRuleText(rule: CSSStyleRule): { selector: string; declarations: string } | null {
  try {
    const declarations = rule.style.cssText.trim();
    if (!declarations) return null;
    const selectors = splitSelectorList(rule.selectorText.trim());
    if (!selectors) return null;
    const rewritten = selectors.map(rewriteSelector);
    if (rewritten.some((selector) => !selector)) return null;
    return { selector: rewritten.join(", "), declarations };
  } catch {
    return null;
  }
}

function isCompatibleContainerType(value: string): boolean {
  const normalized = value.trim().toLowerCase();
  return normalized === "" || normalized === "normal" || normalized === "inline-size" || normalized === "size";
}

function hasContainerSizeContainment(value: string): boolean {
  const normalized = value.trim().toLowerCase();
  return normalized === "inline-size" || normalized === "size";
}

function containerNames(value: string): string {
  const names = value.trim();
  return names.toLowerCase() === "none" ? "" : names;
}

function restoreInlineProperty(
  style: CSSStyleDeclaration,
  property: string,
  original: { value: string; priority: string },
  ownedValue: string,
  ownedPriority: string,
): void {
  if (style.getPropertyValue(property) !== ownedValue || style.getPropertyPriority(property) !== ownedPriority) return;
  if (original.value) style.setProperty(property, original.value, original.priority);
  else style.removeProperty(property);
}

class HostDockingControllerImpl implements HostDockingController {
  private readonly diagnostics = emptyDiagnostics();
  private readonly records = new Map<CSSStyleSheet, SourceRecord>();
  private readonly rootProperties = new Map<string, { value: string; priority: string; original: { value: string; priority: string } }>();
  private readonly containerName = `${HOST_DOCKING_CONTAINER_NAME}-${++nextContainerId}`;
  private desktopLeaseCount = 0;
  private geometryActive = false;
  private conversionInstalled = false;
  private observing = false;
  private budgetBreached = false;
  private ownsRootDataAttribute = false;
  private rootConflictReported = false;
  private observer: MutationObserver | null = null;
  private scheduledTimer: ReturnType<typeof setTimeout> | null = null;
  private reconciliationTimer: ReturnType<typeof setInterval> | null = null;
  private generatedRuleCount = 0;
  private generatedCssCharacters = 0;
  private candidateRulesSeen = 0;
  private totalProcessingMilliseconds = 0;
  private reconciliationCursor = 0;

  constructor(private readonly documentRef: Document, private readonly options: HostDockingOptions = {}) {}

  acquireLease(initialState: HostDockingLeaseState = { open: false, desktop: false }): HostDockingLease {
    let released = false;
    let state: HostDockingLeaseState = { open: false, desktop: false };
    const sync = (nextState: HostDockingLeaseState) => {
      if (released) return;
      const wasQualifying = state.open && state.desktop;
      state = { ...nextState };
      const isQualifying = state.open && state.desktop;
      if (wasQualifying === isQualifying) return;
      this.desktopLeaseCount += isQualifying ? 1 : -1;
      this.syncDesktopState();
    };
    sync(initialState);
    return {
      setState: sync,
      update: sync,
      release: () => {
        if (released) return;
        released = true;
        if (state.open && state.desktop) {
          this.desktopLeaseCount -= 1;
          this.syncDesktopState();
        }
      },
    };
  }

  reconcileNow(): void {
    if (!this.geometryActive || this.budgetBreached) return;
    this.reconcile();
  }

  getDiagnostics(): Readonly<HostDockingDiagnostics> {
    return { ...this.diagnostics };
  }

  getSnapshot(): HostDockingSnapshot {
    return {
      desktopLeaseCount: this.desktopLeaseCount,
      geometryActive: this.geometryActive,
      conversionInstalled: this.conversionInstalled,
      generatedRuleCount: this.generatedRuleCount,
      observing: this.observing,
      diagnostics: this.getDiagnostics(),
    };
  }

  private report(key: keyof HostDockingDiagnostics): void {
    this.diagnostics[key] += 1;
    try {
      this.options.onDiagnostics?.(this.getDiagnostics());
    } catch {
      // Local diagnostics must not interfere with host layout.
    }
  }

  private syncDesktopState(): void {
    if (this.desktopLeaseCount > 0) {
      if (!this.geometryActive) this.geometryActive = this.applyGeometry();
      if (this.geometryActive && !this.budgetBreached) {
        this.startObservation();
        this.scheduleReconciliation();
      }
      return;
    }
    this.geometryActive = false;
    this.stopObservation();
    this.removeGeometry();
  }

  private applyGeometry(): boolean {
    const root = this.documentRef.documentElement;
    if (!root || (root.hasAttribute(HOST_DOCKING_DATA_ATTRIBUTE) && root.getAttribute(HOST_DOCKING_DATA_ATTRIBUTE) !== "active")) {
      return this.reportRootConflict();
    }
    const style = root.style;
    const computedStyle = this.documentRef.defaultView?.getComputedStyle(root);
    const computedContainerType =
      computedStyle?.getPropertyValue("container-type") ?? style.getPropertyValue("container-type");
    if (!isCompatibleContainerType(computedContainerType)) return this.reportRootConflict();
    if (style.getPropertyValue("inline-size").trim() && !this.rootProperties.has("inline-size")) return this.reportRootConflict();
    if (this.rootProperties.size && !this.ownedRootValuesAreUnchanged()) return this.reportRootConflict();

    const scrollbarWidth = this.scrollbarWidth();
    const inlineSize = `calc(100vw - ${HOST_DOCKING_RAIL_PX}px${scrollbarWidth ? ` - ${scrollbarWidth}px` : ""})`;
    this.setOwnedRootProperty("inline-size", inlineSize);
    if (!hasContainerSizeContainment(computedContainerType)) this.setOwnedRootProperty("container-type", "inline-size");
    if (!this.budgetBreached) {
      const existingNames = containerNames(computedStyle?.getPropertyValue("container-name") ?? style.getPropertyValue("container-name"));
      const names = existingNames ? `${existingNames} ${this.containerName}` : this.containerName;
      this.setOwnedRootProperty("container-name", names);
    }
    if (!root.hasAttribute(HOST_DOCKING_DATA_ATTRIBUTE)) {
      this.ownsRootDataAttribute = true;
      root.setAttribute(HOST_DOCKING_DATA_ATTRIBUTE, "active");
    }
    if (!this.rootGeometryIsEffective(inlineSize, scrollbarWidth)) {
      this.removeGeometry();
      return this.reportRootConflict();
    }
    return true;
  }

  private reportRootConflict(): false {
    if (!this.rootConflictReported) {
      this.rootConflictReported = true;
      this.report("skippedRootConflict");
    }
    return false;
  }

  private scrollbarWidth(): number {
    const view = this.documentRef.defaultView;
    const clientWidth = this.documentRef.documentElement.clientWidth;
    return view?.innerWidth && clientWidth ? Math.max(0, Math.round(view.innerWidth - clientWidth)) : 0;
  }

  private rootGeometryIsEffective(inlineSize: string, scrollbarWidth: number): boolean {
    const view = this.documentRef.defaultView;
    const computedInlineSize = view?.getComputedStyle(this.documentRef.documentElement).getPropertyValue("inline-size").trim();
    if (!computedInlineSize || computedInlineSize === inlineSize) return true;
    const actual = Number.parseFloat(computedInlineSize);
    const expected = (view?.innerWidth ?? 0) - HOST_DOCKING_RAIL_PX - scrollbarWidth;
    return Number.isFinite(actual) && Math.abs(actual - expected) < 1;
  }

  private setOwnedRootProperty(property: string, value: string): void {
    const style = this.documentRef.documentElement.style;
    if (this.rootProperties.has(property)) return;
    const original = { value: style.getPropertyValue(property), priority: style.getPropertyPriority(property) };
    style.setProperty(property, value, original.priority);
    this.rootProperties.set(property, { value: style.getPropertyValue(property), priority: style.getPropertyPriority(property), original });
  }

  private ownedRootValuesAreUnchanged(): boolean {
    const style = this.documentRef.documentElement.style;
    return Array.from(this.rootProperties).every(([property, value]) =>
      style.getPropertyValue(property) === value.value && style.getPropertyPriority(property) === value.priority,
    );
  }

  private removeGeometry(): void {
    const root = this.documentRef.documentElement;
    for (const [property, value] of this.rootProperties) {
      restoreInlineProperty(root.style, property, value.original, value.value, value.priority);
    }
    this.rootProperties.clear();
    if (this.ownsRootDataAttribute && root.getAttribute(HOST_DOCKING_DATA_ATTRIBUTE) === "active") root.removeAttribute(HOST_DOCKING_DATA_ATTRIBUTE);
    this.ownsRootDataAttribute = false;
    this.rootConflictReported = false;
  }

  private startObservation(): void {
    const MutationObserverConstructor = this.documentRef.defaultView?.MutationObserver;
    if (this.observing || this.budgetBreached || !MutationObserverConstructor) return;
    this.observer = new MutationObserverConstructor((records) => {
      if (records.some((record) => this.isRelevantMutation(record))) this.scheduleReconciliation();
    });
    this.observer.observe(this.documentRef, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ["media", "disabled", "href"] });
    this.reconciliationTimer = setInterval(() => this.scheduleReconciliation(), 250);
    this.observing = true;
  }

  private isRelevantMutation(record: MutationRecord): boolean {
    const relevantNode = (node: Node) => {
      if (node.nodeType === 3) return node.parentElement?.matches("style, link[rel='stylesheet']") === true;
      return node.nodeType === 1 && (node as Element).matches("style, link[rel='stylesheet']");
    };
    if (record.type === "attributes" || record.type === "characterData") return relevantNode(record.target) && !isWidgetOwnedElement(record.target.parentElement);
    return Array.from(record.addedNodes).some(relevantNode) || Array.from(record.removedNodes).some(relevantNode);
  }

  private stopObservation(): void {
    this.observer?.disconnect();
    this.observer = null;
    this.observing = false;
    if (this.reconciliationTimer !== null) clearInterval(this.reconciliationTimer);
    if (this.scheduledTimer !== null) clearTimeout(this.scheduledTimer);
    this.reconciliationTimer = null;
    this.scheduledTimer = null;
  }

  private scheduleReconciliation(): void {
    if (!this.geometryActive || this.budgetBreached || this.scheduledTimer !== null) return;
    this.scheduledTimer = setTimeout(() => {
      this.scheduledTimer = null;
      this.reconcile();
    }, 0);
  }

  private reconcile(): void {
    if (!this.geometryActive || this.budgetBreached) return;
    const startedAt = now();
    this.candidateRulesSeen = 0;
    const sheets = Array.from(this.documentRef.styleSheets).filter((sheet): sheet is CSSStyleSheet => !isWidgetOwnedStylesheet(sheet));
    if (sheets.length > HOST_DOCKING_LIMITS.maxStylesheets) return this.reportBudget();
    const liveSheets = new Set(sheets);
    for (const [sheet, record] of this.records) {
      if (!liveSheets.has(sheet) || !this.sourceOwner(sheet)?.isConnected) this.removeRecord(sheet, record);
    }
    const startIndex = this.reconciliationCursor % sheets.length;
    for (let offset = 0; offset < sheets.length; offset += 1) {
      const index = (startIndex + offset) % sheets.length;
      if (now() - startedAt >= HOST_DOCKING_LIMITS.maxBatchMilliseconds) {
        this.reconciliationCursor = index;
        break;
      }
      const sheet = sheets[index];
      this.reconcileSource(sheet);
      this.reconciliationCursor = (index + 1) % sheets.length;
      if (this.budgetBreached) return;
    }
    this.totalProcessingMilliseconds += now() - startedAt;
    if (this.totalProcessingMilliseconds >= HOST_DOCKING_LIMITS.maxTotalProcessingMilliseconds) this.reportBudget();
  }

  private sourceOwner(sheet: CSSStyleSheet): HTMLElement | null {
    const owner = sheet.ownerNode;
    return owner && owner.nodeType === 1 ? (owner as HTMLElement) : null;
  }

  private reconcileSource(sheet: CSSStyleSheet): void {
    const owner = this.sourceOwner(sheet);
    if (!owner || !owner.parentNode || isWidgetOwnedElement(owner)) return;
    const media = owner.getAttribute("media")?.trim().toLowerCase() || "all";
    if (sheet.disabled || owner.hasAttribute("disabled") || !["all", "screen"].includes(media)) {
      return this.removeRecord(sheet, this.records.get(sheet));
    }
    let rules: CSSRuleList;
    try {
      rules = sheet.cssRules;
    } catch {
      this.report("skippedUnreadable");
      this.removeRecord(sheet);
      return;
    }
    const compiled = this.compileSource(rules);
    if (!compiled) {
      if (!this.budgetBreached) this.removeRecord(sheet);
      return;
    }
    const existing = this.records.get(sheet);
    if (existing?.fingerprint === compiled.fingerprint && existing.generatedStyle.isConnected) {
      if (existing.generatedStyle.parentNode === owner.parentNode && owner.nextSibling === existing.generatedStyle) return;
      owner.parentNode.insertBefore(existing.generatedStyle, owner.nextSibling);
      return;
    }
    if (compiled.css.length + this.generatedCssCharacters - (existing?.cssCharacters ?? 0) > HOST_DOCKING_LIMITS.maxGeneratedCssCharacters) {
      return this.reportBudget();
    }
    if (!existing && this.records.size >= HOST_DOCKING_LIMITS.maxGeneratedStyles) return this.reportBudget();
    try {
      const generatedStyle = this.documentRef.createElement("style");
      generatedStyle.setAttribute(HOST_DOCKING_STYLE_ATTRIBUTE, "true");
      generatedStyle.setAttribute("data-phoebe-widget-owned", "true");
      if (media === "screen") generatedStyle.media = "screen";
      generatedStyle.textContent = compiled.css;
      owner.parentNode.insertBefore(generatedStyle, existing?.generatedStyle?.isConnected ? existing.generatedStyle : owner.nextSibling);
      existing?.generatedStyle.remove();
      this.generatedCssCharacters += compiled.css.length - (existing?.cssCharacters ?? 0);
      this.generatedRuleCount += compiled.ruleCount - (existing?.ruleCount ?? 0);
      this.records.set(sheet, { fingerprint: compiled.fingerprint, generatedStyle, cssCharacters: compiled.css.length, ruleCount: compiled.ruleCount, failures: 0 });
      this.conversionInstalled ||= compiled.ruleCount > 0;
      this.report("converted");
    } catch {
      this.report("failedInsertion");
    }
  }

  private compileSource(rules: CSSRuleList): { fingerprint: string; css: string; ruleCount: number } | null {
    const output: string[] = [];
    const fingerprints: string[] = [];
    let ruleCount = 0;
    for (let index = 0; index < rules.length; index += 1) {
      const rule = rules.item(index);
      if (!rule || ++this.candidateRulesSeen > HOST_DOCKING_LIMITS.maxCandidateRules) {
        this.reportBudget();
        return null;
      }
      if (isStyleRule(rule)) {
        if (hasNestedRules(rule)) return this.skipUnsupported();
        const safe = safeRuleText(rule);
        if (!safe) return this.skipUnsupported();
        if (hasResourceCapableValue(safe.declarations)) return this.skipResource();
        if (hasViewportWidthUnit(safe.declarations)) return this.skipViewport();
        output.push(`@container ${this.containerName} (min-width: 0px) { ${safe.selector} { ${safe.declarations} } }`);
        fingerprints.push(`style:${index}:${rule.cssText}`);
        ruleCount += 1;
        continue;
      }
      if (!isMediaRule(rule)) return this.skipUnsupported();
      const condition = supportedContainerCondition(rule.media.mediaText);
      if (!condition) return this.skipUnsupported();
      for (let childIndex = 0; childIndex < rule.cssRules.length; childIndex += 1) {
        const child = rule.cssRules.item(childIndex);
        if (!child || ++this.candidateRulesSeen > HOST_DOCKING_LIMITS.maxCandidateRules) {
          this.reportBudget();
          return null;
        }
        if (!isStyleRule(child)) return this.skipUnsupported();
        if (hasNestedRules(child)) return this.skipUnsupported();
        const safe = safeRuleText(child);
        if (!safe) return this.skipUnsupported();
        if (hasResourceCapableValue(safe.declarations)) return this.skipResource();
        if (hasViewportWidthUnit(safe.declarations)) return this.skipViewport();
        output.push(`@container ${this.containerName} ${condition} { ${safe.selector} { ${safe.declarations} } }`);
        fingerprints.push(`media:${index}:${childIndex}:${rule.media.mediaText}:${child.cssText}`);
        ruleCount += 1;
      }
    }
    return { fingerprint: fingerprints.join("\u001f"), css: output.join("\n"), ruleCount };
  }

  private skipUnsupported(): null { this.report("skippedUnsupported"); return null; }
  private skipResource(): null { this.report("skippedResourceValue"); return null; }
  private skipViewport(): null { this.report("skippedViewportUnit"); return null; }

  private removeRecord(sheet: CSSStyleSheet, record = this.records.get(sheet)): void {
    if (!record) return;
    record.generatedStyle.remove();
    this.generatedCssCharacters -= record.cssCharacters;
    this.generatedRuleCount -= record.ruleCount;
    this.records.delete(sheet);
  }

  private reportBudget(): void {
    if (this.budgetBreached) return;
    this.budgetBreached = true;
    for (const [sheet, record] of this.records) this.removeRecord(sheet, record);
    this.stopObservation();
    this.report("skippedBudget");
  }
}

export function getHostDockingController(documentRef?: Document, options?: HostDockingOptions): HostDockingController | null {
  if (!canUseDocument(documentRef)) return null;
  const existing = controllerByDocument.get(documentRef);
  if (existing) return existing;
  const controller = new HostDockingControllerImpl(documentRef, options);
  controllerByDocument.set(documentRef, controller);
  return controller;
}

export function acquireHostDockingLease(
  documentRef: Document | undefined,
  initialState: HostDockingLeaseState = { open: false, desktop: false },
  options?: HostDockingOptions,
): HostDockingLease {
  return getHostDockingController(documentRef, options)?.acquireLease(initialState) ?? { setState() {}, update() {}, release() {} };
}
